/** Same-origin report photos and clickable report URLs. */

export type ReportPhoto = {
  src: string;
  alt: string;
};

export const escapeHtml = (value: string): string =>
  String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

export const looksLikeReportPhotoUrl = (value: string): boolean => {
  const blob = String(value || '').trim();
  if (!blob) return false;
  if (/\/api\/v1\/research\/media\//i.test(blob)) return true;
  if (/\.(jpe?g|png|gif|webp)(\?|$)/i.test(blob)) return true;
  return /fbcdn|scontent|cdninstagram|googleusercontent|licdn\.com|linkedinusercontent|upload\.wikimedia|fbsbx|twimg|tiktokcdn|ggpht/i.test(blob);
};

export const normalizeSocialProfileUrl = (url: string): string =>
  String(url || '')
    .trim()
    .toLowerCase()
    .replace(/^https?:\/\/(www\.)?/, 'https://')
    .replace(/\/+$/, '');

export const socialProfileUrlsMatch = (left: string, right: string): boolean => {
  const a = normalizeSocialProfileUrl(left);
  const b = normalizeSocialProfileUrl(right);
  if (!a || !b) return false;
  return a === b || a.startsWith(`${b}/`) || b.startsWith(`${a}/`);
};

export const socialPlatformOfUrl = (url: string): string | null => {
  const host = normalizeSocialProfileUrl(url);
  if (host.includes('facebook.com')) return 'facebook';
  if (host.includes('instagram.com')) return 'instagram';
  if (host.includes('linkedin.com')) return 'linkedin';
  if (host.includes('tiktok.com')) return 'tiktok';
  if (host.includes('x.com') || host.includes('twitter.com')) return 'x';
  return null;
};

const collectHandleUrls = (handles: unknown): string[] => {
  if (!handles || typeof handles !== 'object') return [];
  return Object.values(handles as Record<string, { url?: string }>)
    .map((row) => String(row?.url || '').trim())
    .filter(Boolean);
};

const collectSelectionUrls = (selection: unknown, key: 'included_accounts' | 'excluded_accounts'): string[] => {
  if (!selection || typeof selection !== 'object') return [];
  const rows = (selection as Record<string, unknown>)[key];
  if (!Array.isArray(rows)) return [];
  return rows
    .map((row) => (row && typeof row === 'object' ? String((row as { url?: string }).url || '').trim() : ''))
    .filter(Boolean);
};

export const collectSelectedSocialProfileUrls = (live: Record<string, unknown> | null | undefined): string[] => {
  if (!live || typeof live !== 'object') return [];
  return [
    ...collectHandleUrls(live.social_handles),
    ...collectSelectionUrls(live.social_account_selection, 'included_accounts'),
    ...collectSelectionUrls(live.social_handle_resolution, 'included_accounts'),
  ];
};

export const collectExcludedSocialProfileUrls = (
  live: Record<string, unknown> | null | undefined,
  selected: string[] = [],
): string[] => {
  if (!live || typeof live !== 'object') return [];
  const explicit = [
    ...collectSelectionUrls(live.social_account_selection, 'excluded_accounts'),
    ...collectSelectionUrls(live.social_handle_resolution, 'excluded_accounts'),
  ];
  if (explicit.length) return explicit;
  if (!selected.length) return [];
  const probes = Array.isArray(live.direct_profile_probes) ? live.direct_profile_probes : [];
  const excluded: string[] = [];
  for (const probe of probes) {
    if (!probe || typeof probe !== 'object') continue;
    const url = String((probe as { url?: string; final_url?: string }).url
      || (probe as { final_url?: string }).final_url
      || '').trim();
    if (!url) continue;
    if (selected.some((row) => socialProfileUrlsMatch(url, row))) continue;
    const platform = socialPlatformOfUrl(url);
    if (!platform) continue;
    if (selected.some((row) => socialPlatformOfUrl(row) === platform)) excluded.push(url);
  }
  return excluded;
};

export const isSocialHubPhotoSource = (url: string): boolean => {
  const blob = String(url || '').trim().toLowerCase();
  if (!blob) return false;
  if (/facebook\.com\/(?:friends|watch|marketplace|groups|people|pages|photo\.php)(?:\/|$|\?)/i.test(blob)) {
    return true;
  }
  if (/facebook\.com\/[^/?#]+\/friends(?:\/|$|\?)/i.test(blob)) return true;
  return false;
};

export const socialPhotoSourceAllowed = (
  sourceUrl: string | null | undefined,
  selected: string[],
  excluded: string[],
): boolean => {
  const source = String(sourceUrl || '').trim();
  if (source && isSocialHubPhotoSource(source)) return false;
  if (source && excluded.some((url) => socialProfileUrlsMatch(source, url))) return false;
  if (!selected.length) return true;
  if (!source) return false;
  return selected.some((url) => socialProfileUrlsMatch(source, url));
};

export const looksLikeSocialChromePhoto = (url: string): boolean => {
  const blob = String(url || '').toLowerCase();
  if (!blob) return true;
  if (/rsrc\.php|s50x50|s60x60|s40x40|fb_icon|linkedin\.com\/favicon|glyph|ig_brand|ig_glyph|appicon|sprite|\/images\/instagram\/|instagram\.com\/static/.test(blob)) {
    return true;
  }
  if (/instagram\.com\/(?:p|reel|stories)\//.test(blob)) return true;
  if (/facebook\.com\/photo\.php|facebook\.com\/[^/?#]+\/photos\//.test(blob) && !/\.(jpe?g|png|gif|webp)(\?|$)/i.test(blob)) return true;
  if (/\.html?(\?|$)/.test(blob)) return true;
  return /^https?:\/\/scontent[^/\s]*\.cdninstagram\.com\/?$/.test(blob);
};

type IdentityPhotoReport = {
  content?: string;
  sourceChecksRuns?: unknown[];
  checkResults?: unknown[];
  reportEvidence?: {
    photoUrls?: string[];
    photoNotes?: string[];
    courts?: Array<{ title: string; detail: string }>;
    contacts?: Array<{ label: string; value: string }>;
    affiliations?: Array<{ kind: string; value: string }>;
    family?: Array<{ name: string; relation: string }>;
    friendNote?: string;
  } | null;
  keyData?: {
    addresses?: string;
    phones?: string;
    emails?: string;
    locationCounty?: string;
    locationCity?: string;
    locationRegion?: string;
  } | null;
  factDatabase?: {
    signals?: Array<{ fact_type?: string; value?: unknown; decision?: string }>;
    entity?: { social_handles?: unknown; addresses?: string[]; phones?: string[]; emails?: string[] };
    records?: { court_cases?: Array<Record<string, unknown>> };
    sourceCheckFacts?: string[];
  } | null;
  criticalFacts?: { items?: Array<{ label?: string; value?: string | null; status?: string }> };
};

export const collectIdentityPhotosFromReport = (
  report: IdentityPhotoReport,
  options?: { mediaBaseUrl?: string; accessToken?: string; keepRawUrl?: boolean },
): Array<{ src: string; alt: string }> => {
  const photos: Array<{ src: string; alt: string }> = [];
  const seen = new Set<string>();
  const rejected = new Set<string>();
  const reject = (url: string) => {
    const key = String(url || '').trim().split('?')[0].toLowerCase();
    if (key) rejected.add(key);
  };
  const push = (url: string, alt = 'photo') => {
    const raw = String(url || '').trim();
    if (!raw || looksLikeSocialChromePhoto(raw) || isSocialHubPhotoSource(raw)) return;
    if (rejected.has(raw.split('?')[0].toLowerCase())) return;
    const leaf = decodeURIComponent(raw.split('?')[0].split('/').pop() || '');
    if (/^(photo|photos)(?:\.html?)?$/i.test(leaf)) return;
    const base = String(options?.mediaBaseUrl || '').replace(/\/$/, '');
    const token = String(options?.accessToken || '').trim();
    let href = raw;
    if (/^https?:\/\//i.test(raw) && !/\/api\/v1\/research\/media/.test(raw)) {
      href = `${base}/api/v1/research/media/proxy?url=${encodeURIComponent(raw)}`;
    } else if (raw.startsWith('/api/v1/research/media')) {
      href = `${base}${raw}`;
    }
    if (token && /\/api\/v1\/research\/media/.test(href) && !options?.keepRawUrl) {
      href += `${href.includes('?') ? '&' : '?'}access_token=${encodeURIComponent(token)}`;
    }
    if (options?.keepRawUrl) href = raw;
    const key = raw.split('?')[0].toLowerCase();
    if (!key || seen.has(key)) return;
    seen.add(key);
    photos.push({ src: href, alt });
  };
  for (const url of report.reportEvidence?.photoUrls || []) push(String(url || ''), 'photo');
  const takeFromRecord = (item: Record<string, unknown> | null | undefined, alt: string) => {
    if (!item) return;
    const contentType = String(item.content_type || '').toLowerCase();
    const status = String(item.preservation_status || '').toLowerCase();
    if (contentType.includes('text/html') || contentType.startsWith('text/') || status === 'skipped_html') return;
    for (const key of ['headshot_url', 'image_url', 'profile_pic_url', 'profile_picture', 'picture', 'avatar', 'photo_url', 'og_image', 'media_url', 'logo_url', 'cover_url', 'storage_url', 'public_url', 'thumbnail_url', 'display_url', 'src', 'uri']) {
      const value = item[key];
      if (typeof value !== 'string') continue;
      if (looksLikeReportPhotoUrl(value)) {
        push(value, photoAltLabel(alt));
      }
    }
  };
  const runs = [
    ...(Array.isArray(report.sourceChecksRuns) ? report.sourceChecksRuns : []),
    ...(Array.isArray(report.checkResults) ? report.checkResults : []),
  ];
  const selectedUrls: string[] = [];
  const excludedUrls: string[] = [];
  for (const row of runs) {
    if (!row || typeof row !== 'object') continue;
    const record = row as Record<string, unknown>;
    const live = record.live_result && typeof record.live_result === 'object'
      ? record.live_result as Record<string, unknown>
      : record;
    selectedUrls.push(...collectSelectedSocialProfileUrls(live));
    excludedUrls.push(...collectExcludedSocialProfileUrls(live, selectedUrls));
  }
  const walkLive = (live: Record<string, unknown>) => {
    takeFromRecord(live, 'photo');
    const wiki = live.wikipedia && typeof live.wikipedia === 'object'
      ? live.wikipedia as Record<string, unknown>
      : null;
    takeFromRecord(wiki, 'photo');
    const probes = Array.isArray(live.direct_profile_probes) ? live.direct_profile_probes : [];
    for (const probe of probes) {
      if (!probe || typeof probe !== 'object') continue;
      const row = probe as Record<string, unknown>;
      const probeUrl = String(row.url || row.final_url || '');
      const hub = isSocialHubPhotoSource(probeUrl);
      if (probeUrl && !hub && !socialPhotoSourceAllowed(probeUrl, selectedUrls, excludedUrls)) continue;
      const ig = row.instagram_profile && typeof row.instagram_profile === 'object' ? row.instagram_profile as Record<string, unknown> : null;
      const fb = row.facebook_profile && typeof row.facebook_profile === 'object' ? row.facebook_profile as Record<string, unknown> : null;
      const li = row.linkedin_profile && typeof row.linkedin_profile === 'object' ? row.linkedin_profile as Record<string, unknown> : null;
      const signals = row.page_signals && typeof row.page_signals === 'object' ? row.page_signals as Record<string, unknown> : null;
      takeFromRecord(ig, 'headshot');
      takeFromRecord(fb, 'headshot');
      takeFromRecord(li, 'headshot');
      takeFromRecord(signals, 'photo');
      takeFromRecord(row, 'photo');
      for (const listed of [
        ...(Array.isArray(fb?.photos) ? fb.photos : []),
        ...(Array.isArray(ig?.media) ? ig.media : []),
        ...(Array.isArray(ig?.posts) ? ig.posts : []),
      ]) {
        if (typeof listed === 'string') push(listed, 'photo');
        else if (listed && typeof listed === 'object') {
          const row = listed as Record<string, unknown>;
          const image = row.image && typeof row.image === 'object' ? row.image as Record<string, unknown> : null;
          takeFromRecord(image, 'photo');
          takeFromRecord(row, 'photo');
        }
      }
    }
    for (const profile of Array.isArray(live.structured_profiles) ? live.structured_profiles : []) {
      if (!profile || typeof profile !== 'object') continue;
      const card = profile as Record<string, unknown>;
      const extracted = card.extracted && typeof card.extracted === 'object'
        ? card.extracted as Record<string, unknown>
        : null;
      takeFromRecord(card, 'headshot');
      takeFromRecord(extracted, 'headshot');
      for (const listed of [
        ...(Array.isArray(card.photos) ? card.photos : []),
        ...(Array.isArray(extracted?.photos) ? extracted.photos : []),
        ...(Array.isArray(extracted?.images) ? extracted.images : []),
      ]) {
        if (typeof listed === 'string') push(listed, 'headshot');
        else if (listed && typeof listed === 'object') takeFromRecord(listed as Record<string, unknown>, 'headshot');
      }
    }
    const preview = Array.isArray(live.confirmed_profiles_preview) ? live.confirmed_profiles_preview : [];
    for (const item of preview) {
      if (item && typeof item !== 'object') continue;
      const previewUrl = String((item as Record<string, unknown>).url || (item as Record<string, unknown>).profile_url || '');
      if (previewUrl && !socialPhotoSourceAllowed(previewUrl, selectedUrls, excludedUrls)) continue;
      takeFromRecord(item as Record<string, unknown>, 'photo');
    }
    const capture = live.media_evidence_capture && typeof live.media_evidence_capture === 'object'
      ? live.media_evidence_capture as Record<string, unknown>
      : null;
    const face = live.face_identity && typeof live.face_identity === 'object'
      ? live.face_identity as Record<string, unknown>
      : null;
    const assets = [
      ...(Array.isArray(live.media_assets) ? live.media_assets : []),
      ...(Array.isArray(capture?.media_assets) ? capture.media_assets : []),
      ...(Array.isArray(face?.persisted_people_photos) ? face.persisted_people_photos : []),
    ];
    for (const asset of assets) {
      if (!asset || typeof asset !== 'object') continue;
      const sourceUrl = String((asset as Record<string, unknown>).source_url || (asset as Record<string, unknown>).profile_url || '');
      if (sourceUrl && !socialPhotoSourceAllowed(sourceUrl, selectedUrls, excludedUrls)) {
        for (const key of ['media_url', 'url', 'image_url', 'photo_url', 'headshot_url']) {
          const value = (asset as Record<string, unknown>)[key];
          if (typeof value === 'string') reject(value);
        }
        continue;
      }
      takeFromRecord(asset as Record<string, unknown>, 'photo');
    }
  };
  for (const row of runs) {
    if (!row || typeof row !== 'object') continue;
    const record = row as Record<string, unknown>;
    const live = record.live_result && typeof record.live_result === 'object'
      ? record.live_result as Record<string, unknown>
      : record;
    walkLive(live);
  }
  const hasStructuredLive = runs.some((row) => {
    if (!row || typeof row !== 'object') return false;
    const record = row as Record<string, unknown>;
    const live = record.live_result && typeof record.live_result === 'object'
      ? record.live_result as Record<string, unknown>
      : record;
    return Boolean(
      live.direct_profile_probes
      || live.media_assets
      || live.face_identity
      || live.social_handles,
    );
  });
  for (const match of String(report.content || '').matchAll(/!\[[^\]]*\]\(([^)\s]+)\)/g)) {
    push(match[1], 'photo');
  }
  const signals = Array.isArray(report.factDatabase?.signals) ? report.factDatabase?.signals : [];
  const skipUnattributedFactPhotos = selectedUrls.length > 0 || hasStructuredLive;
  for (const signal of signals || []) {
    const kind = String(signal?.fact_type || '').toLowerCase();
    const value = typeof signal?.value === 'string' ? signal.value.trim() : '';
    if (!(kind.includes('headshot') || kind.includes('photo')) || !value) continue;
    if (skipUnattributedFactPhotos && !looksLikeReportPhotoUrl(value)) continue;
    push(value, 'photo');
  }
  return photos.slice(0, 36);
};

export type ReportCourtRow = { title: string; detail: string };
export type ReportFamilyRow = { name: string; relation: string };

const asRecord = (value: unknown): Record<string, unknown> | null => (
  value && typeof value === 'object' ? value as Record<string, unknown> : null
);

const personNameOf = (value: unknown): string => {
  if (typeof value === 'string' || typeof value === 'number') return String(value).trim();
  const row = asRecord(value);
  if (!row) return '';
  const joined = [row.first_name, row.middle_name, row.last_name].map((part) => String(part || '').trim()).filter(Boolean).join(' ');
  return String(row.name || row.full_name || row.value || joined || '').trim();
};

const RELATION_LABEL = /^(father|mother|parent|parents|spouse|wife|husband|child|son|daughter|sibling|brother|sister|other|person|relative|family|friend|people)$/i;

const looksLikePersonName = (value: string): boolean => {
  const cleaned = String(value || '').replace(/\s+/g, ' ').trim();
  if (cleaned.length < 5 || cleaned.length > 80) return false;
  if (/[_:]|https?:|familysearch|results for|historical_record|was searched|source check|people-finder|this pass|profile views|discover who/i.test(cleaned)) {
    return false;
  }
  const words = cleaned.replace(/\([^)]*\)/g, ' ').trim().split(/\s+/).filter(Boolean);
  if (words.length < 2 || words.length > 6) return false;
  if (!words.every((word) => /^[A-Za-z][A-Za-z'.-]{0,30}$/.test(word))) return false;
  if (words.filter((word) => RELATION_LABEL.test(word)).length >= 2) return false;
  if (words.some((word) => /[a-z][A-Z]/.test(word) && word.replace(/[^A-Za-z]/g, '').length > 6)) return false;
  const given = words.slice(0, -1);
  if (given.some((word) => word.replace(/\./g, '').length < 3 && !/^[A-Za-z]\.$/.test(word))) return false;
  if (/\b[A-Z]{2,}[a-z]{1,8},\s*[A-Z]{2}\b/.test(cleaned)) return false;
  return true;
};

const explicitFamilyRelation = (relation: string): string => {
  const text = String(relation || '');
  if (/\b(wife|husband|spouse)\b/i.test(text)) return 'Spouse';
  if (/\b(mother|father|parent)\b/i.test(text)) return 'Parent';
  if (/\b(son|daughter|child)\b/i.test(text)) return 'Child';
  if (/\b(brother|sister|sibling)\b/i.test(text)) return 'Sibling';
  return '';
};

const birthYearOf = (value: unknown): number | null => {
  const match = String(value || '').match(/\b((?:19|20)\d{2})\b/);
  return match ? Number(match[1]) : null;
};

const subjectLastName = (name: string): string => {
  const tokens = String(name || '').toLowerCase().split(/[^a-z]+/).filter((token) => token.length >= 2);
  return tokens.length >= 2 ? tokens[tokens.length - 1] : '';
};

const sharesSurname = (name: string, subject: string): boolean => {
  const last = subjectLastName(subject);
  if (!last) return false;
  const tokens = String(name || '').toLowerCase().split(/[^a-z]+/).filter((token) => token.length >= 2);
  return tokens.length >= 2 && tokens[tokens.length - 1] === last;
};

const sectionBullets = (content: string | undefined, heading: string): string[] => {
  const escaped = heading.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const match = String(content || '').match(new RegExp(`(?:^|\\n)###\\s+${escaped}\\s*\\n([\\s\\S]*?)(?=\\n###\\s+|\\n##\\s+|$)`, 'i'));
  if (!match) return [];
  return match[1]
    .split('\n')
    .map((line) => line.replace(/^[-*]\s+/, '').trim())
    .filter((line) => Boolean(line) && !/^#{1,6}\s/.test(line));
};

const friendNoteFrom = (report: IdentityPhotoReport): string => {
  const stored = String(report.reportEvidence?.friendNote || '').trim();
  if (stored) return stored;
  const match = String(report.content || '').match(/(?:Facebook friends|Social friends scraped:)\s*([\d,]+)(?:\s+friends)?\s*\(names not captured\)/i);
  if (!match) return '';
  return `${match[1].replace(/,/g, '')} friends (names not captured)`;
};

export const collectReportCourtRows = (report: IdentityPhotoReport & {
  factDatabase?: IdentityPhotoReport['factDatabase'] & {
    records?: { court_cases?: Array<Record<string, unknown>> };
  };
}): ReportCourtRow[] => {
  const rows: ReportCourtRow[] = [];
  const seen = new Set<string>();
  const push = (title: string, detail = '') => {
    const heading = String(title || '').trim();
    if (!heading) return;
    const key = heading.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    rows.push({ title: heading, detail: String(detail || '').trim() });
  };
  for (const row of report.reportEvidence?.courts || []) push(row.title, row.detail);
  for (const line of sectionBullets(report.content, 'Court records')) push(line);
  const cases = report.factDatabase?.records?.court_cases;
  if (Array.isArray(cases)) {
    for (const row of cases) {
      const record = asRecord(row);
      if (!record) continue;
      const number = String(record.case_number || '').trim();
      const name = String(record.case_name || '').trim();
      const detail = [record.filed_at, record.court, record.case_type, record.subject_role, record.case_status]
        .map((part) => String(part || '').trim())
        .filter(Boolean)
        .join(' · ');
      push([number, name].filter(Boolean).join(' — '), detail);
    }
  }
  const runs = [
    ...(Array.isArray(report.sourceChecksRuns) ? report.sourceChecksRuns : []),
    ...(Array.isArray(report.checkResults) ? report.checkResults : []),
  ];
  for (const row of runs) {
    const record = asRecord(row);
    if (!record) continue;
    const live = asRecord(record.live_result) || record;
    const portal = asRecord(live.court_portal_automation);
    const buckets = [
      live.court_cases,
      live.cases,
      live.case_details,
      live.dockets,
      live.results,
      live.matches,
      live.results_preview,
      asRecord(live.records)?.court_cases,
      portal?.results_preview,
      portal?.case_details,
    ];
    for (const run of [
      ...(Array.isArray(live.runs) ? live.runs : []),
      ...(Array.isArray(portal?.runs) ? portal.runs : []),
    ]) {
      const runRecord = asRecord(run);
      if (!runRecord) continue;
      buckets.push(runRecord.results_preview, runRecord.case_details, runRecord.cases, runRecord.results);
    }
    for (const bucket of buckets) {
      if (!Array.isArray(bucket)) continue;
      for (const item of bucket) {
        const court = asRecord(item);
        if (!court) continue;
        const number = String(court.case_number || court.docket || '').trim();
        const name = String(court.case_name || court.title || court.caption || '').trim();
        if (!number && !String(court.court || '').trim()) continue;
        const detail = [court.court, court.case_type, court.subject_role, court.status || court.case_status]
          .map((part) => String(part || '').trim())
          .filter(Boolean)
          .join(' · ');
        push([number, name].filter(Boolean).join(' — '), detail);
      }
    }
  }
  const signals = Array.isArray(report.factDatabase?.signals) ? report.factDatabase.signals : [];
  for (const signal of signals) {
    const kind = String(signal?.fact_type || '');
    const decision = String((signal as { decision?: string })?.decision || '').toLowerCase();
    if (decision === 'excluded' || decision === 'dismissed') continue;
    if (!/court|docket|case/i.test(kind)) continue;
    const record = asRecord(signal?.value);
    if (record) {
      const number = String(record.case_number || record.docket || '').trim();
      const name = String(record.case_name || record.title || record.caption || '').trim();
      push([number, name].filter(Boolean).join(' — ') || personNameOf(record), String(record.court || ''));
      continue;
    }
    const text = String(signal?.value || '').trim();
    if (text && !/search|portal|no case|not found/i.test(text)) push(text);
  }
  return rows;
};

export type ReportContactRow = { label: string; value: string };
export type ReportAffiliationRow = { kind: string; value: string };

const affiliationKind = (label: string): string => {
  if (/church|congregation|religion/i.test(label)) return 'Church';
  if (/hobby|interest/i.test(label)) return 'Hobby';
  return 'Social group';
};

export const collectReportAffiliationRows = (report: IdentityPhotoReport): ReportAffiliationRow[] => {
  const rows: ReportAffiliationRow[] = [];
  const seen = new Set<string>();
  const push = (kind: string, value: string) => {
    const text = String(value || '').replace(/\s+/g, ' ').trim();
    if (!text || text.length < 3 || /not found|no public|profile views|discover who/i.test(text)) return;
    const key = `${kind}:${text}`.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    rows.push({ kind, value: text });
  };
  for (const row of report.reportEvidence?.affiliations || []) push(row.kind, row.value);
  const signals = Array.isArray(report.factDatabase?.signals) ? report.factDatabase.signals : [];
  for (const signal of signals) {
    const kind = String(signal?.fact_type || '');
    if (!/church|congregation|religion|hobby|interest|social_group|group|affiliation/i.test(kind)) continue;
    push(affiliationKind(kind), typeof signal?.value === 'string' ? signal.value : personNameOf(signal?.value));
  }
  const runs = [
    ...(Array.isArray(report.sourceChecksRuns) ? report.sourceChecksRuns : []),
    ...(Array.isArray(report.checkResults) ? report.checkResults : []),
  ];
  const fields: Array<[string, string]> = [
    ['church', 'Church'],
    ['religious_views', 'Church'],
    ['religion', 'Church'],
    ['congregation', 'Church'],
    ['hobbies', 'Hobby'],
    ['hobby', 'Hobby'],
    ['interests', 'Hobby'],
    ['groups', 'Social group'],
    ['organizations', 'Social group'],
  ];
  const take = (record: Record<string, unknown> | null) => {
    if (!record) return;
    for (const [key, kind] of fields) {
      const raw = record[key];
      const values = Array.isArray(raw) ? raw : [raw];
      for (const item of values) push(kind, personNameOf(item));
    }
  };
  for (const row of runs) {
    const live = asRecord(asRecord(row)?.live_result) || asRecord(row);
    if (!live) continue;
    const checks = asRecord(live.checks);
    for (const check of Object.values(checks || {})) {
      const checkRecord = asRecord(check);
      for (const signal of Array.isArray(checkRecord?.signals) ? checkRecord.signals : []) {
        take(asRecord(signal));
      }
    }
    for (const probe of Array.isArray(live.direct_profile_probes) ? live.direct_profile_probes : []) {
      take(asRecord(asRecord(probe)?.facebook_profile));
    }
    for (const profile of Array.isArray(live.structured_profiles) ? live.structured_profiles : []) {
      take(asRecord(asRecord(profile)?.extracted));
    }
  }
  return rows;
};

export const collectReportContactRows = (report: IdentityPhotoReport & {
  factDatabase?: IdentityPhotoReport['factDatabase'] & {
    entity?: { addresses?: string[]; phones?: string[]; emails?: string[] };
    sourceCheckFacts?: string[];
  };
  criticalFacts?: { items?: Array<{ label?: string; value?: string | null; status?: string }> };
}): ReportContactRow[] => {
  const rows: ReportContactRow[] = [];
  const seen = new Set<string>();
  const push = (label: string, value: string) => {
    const text = String(value || '').replace(/\s+/g, ' ').trim();
    if (!text || text.length < 5 || /not found|no public|was searched|this pass/i.test(text)) return;
    const key = `${label}:${text}`.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    rows.push({ label, value: text });
  };
  for (const row of report.reportEvidence?.contacts || []) push(row.label, row.value);
  const intake = report.keyData;
  if (intake) {
    const county = [intake.locationCounty, intake.locationCity, intake.locationRegion].filter(Boolean).join(', ');
    const address = String(intake.addresses || '').trim() || county;
    if (address) push('Address', `${address} (intake)`);
    String(intake.phones || '').split(/[,;\n]+/).forEach((phone) => push('Phone', phone));
    String(intake.emails || '').split(/[,;\n]+/).forEach((email) => push('Email', email));
  }
  for (const line of sectionBullets(report.content, 'Addresses')) push('Address', line);
  for (const line of sectionBullets(report.content, 'Emails and phones')) {
    if (line.includes('@')) push('Email', line);
    else if (/\d{3}/.test(line)) push('Phone', line);
  }
  const entity = report.factDatabase?.entity;
  for (const row of entity?.addresses || []) push('Address', String(row || ''));
  for (const row of entity?.phones || []) push('Phone', String(row || ''));
  for (const row of entity?.emails || []) push('Email', String(row || ''));
  const facts = [
    ...(report.factDatabase?.sourceCheckFacts || []),
    ...((report.criticalFacts?.items || []).map((item) => `${item.label || ''}: ${item.value || ''}`)),
  ];
  const addressKey = (value: string) => String(value || '').toLowerCase().replace(/\(intake(?:\s+form)?\)/g, ' ').replace(/[^a-z0-9]+/g, ' ').trim();
  const confirmedAddresses = new Set((entity?.addresses || []).map((row) => addressKey(String(row || ''))).filter(Boolean));
  const factAddressCounts = new Map<string, number>();
  for (const fact of facts) {
    const match = String(fact || '').match(/^(?:possible\s+)?address\s*:\s*(.+)$/i);
    const key = match ? addressKey(match[1]) : '';
    if (key) factAddressCounts.set(key, (factAddressCounts.get(key) || 0) + 1);
  }
  const birthYear = String(report.factDatabase?.entity && (report.factDatabase.entity as { birthdate?: string }).birthdate || '').match(/\b((?:19|20)\d{2})\b/)?.[1] || '';
  for (const fact of facts) {
    const match = String(fact || '').match(/^(possible\s+)?(address|phone|email|e-mail)\s*:\s*(.+)$/i);
    if (!match) continue;
    const kind = match[2];
    const text = match[3];
    if (/phone/i.test(kind)) {
      push('Phone', text);
      continue;
    }
    if (/e-?mail/i.test(kind)) {
      push('Email', text);
      continue;
    }
    const key = addressKey(text);
    const repeated = (factAddressCounts.get(key) || 0) >= 2;
    const yearOnHit = Boolean(birthYear) && text.includes(birthYear);
    const weak = Boolean(match[1]) || (!confirmedAddresses.has(key) && !repeated && !yearOnHit);
    const label = weak ? 'Possible address' : 'Address';
    push(label, text.replace(/\s*\(intake(?:\s+form)?\)\s*/i, ' '));
  }
  const signals = Array.isArray(report.factDatabase?.signals) ? report.factDatabase.signals : [];
  for (const signal of signals) {
    const kind = String(signal?.fact_type || '');
    const decision = String((signal as { decision?: string })?.decision || '').toLowerCase();
    if (decision === 'excluded' || decision === 'dismissed') continue;
    const text = typeof signal?.value === 'string' ? signal.value : personNameOf(signal?.value);
    if (/address/i.test(kind)) push('Address', text);
    else if (/phone/i.test(kind)) push('Phone', text);
    else if (/email/i.test(kind)) push('Email', text);
  }
  const runs = [
    ...(Array.isArray(report.sourceChecksRuns) ? report.sourceChecksRuns : []),
    ...(Array.isArray(report.checkResults) ? report.checkResults : []),
  ];
  for (const row of runs) {
    const live = asRecord(asRecord(row)?.live_result) || asRecord(row);
    if (!live) continue;
    const takeContact = (record: Record<string, unknown> | null) => {
      if (!record) return;
      const places = [
        ...(Array.isArray(record.addresses) ? record.addresses : []),
        ...(Array.isArray(record.places) ? record.places : []),
        record.address,
      ];
      for (const place of places) push('Address', typeof place === 'string' ? place : personNameOf(place));
      const phones = Array.isArray(record.phones) ? record.phones : [record.phone];
      for (const phone of phones) push('Phone', String(phone || ''));
      const emails = Array.isArray(record.emails) ? record.emails : [record.email];
      for (const email of emails) push('Email', String(email || ''));
    };
    takeContact(asRecord(live.signals));
    for (const profile of Array.isArray(live.structured_profiles) ? live.structured_profiles : []) {
      takeContact(asRecord(asRecord(profile)?.extracted));
    }
    for (const probe of Array.isArray(live.direct_profile_probes) ? live.direct_profile_probes : []) {
      takeContact(asRecord(asRecord(probe)?.facebook_profile));
    }
    const checks = asRecord(live.checks);
    for (const check of Object.values(checks || {})) {
      const checkRecord = asRecord(check);
      for (const signal of Array.isArray(checkRecord?.signals) ? checkRecord.signals : []) {
        takeContact(asRecord(signal));
      }
    }
  }
  return rows;
};

export const collectReportPhotoNotes = (report: IdentityPhotoReport): string[] => {
  const notes: string[] = [];
  const seen = new Set<string>();
  const push = (value: string) => {
    const text = String(value || '').replace(/\s+/g, ' ').trim();
    if (!text || seen.has(text.toLowerCase())) return;
    seen.add(text.toLowerCase());
    notes.push(text);
  };
  for (const note of report.reportEvidence?.photoNotes || []) push(note);
  const runs = [
    ...(Array.isArray(report.sourceChecksRuns) ? report.sourceChecksRuns : []),
    ...(Array.isArray(report.checkResults) ? report.checkResults : []),
  ];
  for (const row of runs) {
    const live = asRecord(asRecord(row)?.live_result) || asRecord(row);
    const face = asRecord(live?.face_identity);
    if (!face) continue;
    const photos = [
      ...(Array.isArray(face.persisted_people_photos) ? face.persisted_people_photos : []),
      ...(Array.isArray(face.people_photos) ? face.people_photos : []),
      ...(Array.isArray(face.accounts) ? face.accounts : []),
    ];
    photos.forEach((asset, index) => {
      const record = asRecord(asset);
      if (!record) return;
      const appearance = asRecord(record.appearance) || record;
      const bits = [
        appearance.apparent_age_group && `apparent age ${appearance.apparent_age_group}`,
        appearance.apparent_ethnicity && `apparent ethnicity ${appearance.apparent_ethnicity}`,
        appearance.hair_color && `hair ${appearance.hair_color}`,
        appearance.eye_color && `eyes ${appearance.eye_color}`,
        appearance.apparent_sex && `sex ${appearance.apparent_sex}`,
        appearance.facial_hair && `facial hair ${appearance.facial_hair}`,
        appearance.glasses && `glasses ${appearance.glasses}`,
        appearance.visual_bmi_band && `visual BMI ${appearance.visual_bmi_band}`,
        appearance.visual_stature && `stature ${appearance.visual_stature}`,
        appearance.notes,
      ].map((part) => String(part || '').trim()).filter((part) => part && !/unknown|n\/a/i.test(part));
      const platform = String(record.platform || appearance.platform || '').trim();
      if (bits.length) push(`Photo ${index + 1}${platform ? ` (${platform})` : ''}: ${bits.join(', ')}`);
    });
    const comparisons = Array.isArray(face.comparisons) ? face.comparisons : [];
    for (const comparison of comparisons) {
      const record = asRecord(comparison);
      if (!record) continue;
      const decision = String(record.decision || '').replace(/_/g, ' ').trim();
      if (!decision) continue;
      const faceInfo = asRecord(record.face);
      const similarity = faceInfo?.similarity;
      const reason = String(record.reason || '').replace(/_/g, ' ').trim();
      const similarityNote = similarity != null && String(similarity).trim() ? `; similarity ${similarity}` : '';
      const reasonNote = reason ? ` (${reason})` : '';
      push(`Facial match: ${record.left_platform || 'account'} vs ${record.right_platform || 'account'} — ${decision}${reasonNote}${similarityNote}`);
    }
  }
  const content = String(report.content || '');
  for (const match of content.matchAll(/(?:photo analysis|personal characteristics|facial match)\s*:\s*([^\n]+)/gi)) {
    String(match[1] || '').split(/\s*;\s*/).forEach((part) => {
      const text = part.replace(/\s+/g, ' ').trim();
      if (!text || /profile views|discover who/i.test(text)) return;
      if (/photo analysis|apparent |hair |eyes |facial match|\bvs\b/i.test(text)) push(text.replace(/\s*\(photo analysis\)\s*$/i, ''));
    });
  }
  return notes.slice(0, 24);
};

const profileAgreesWithSubject = (
  profile: Record<string, unknown>,
  birthYear: number | null,
  intakeState: string,
): boolean => {
  const extracted = asRecord(profile.extracted) || {};
  const year = birthYearOf(profile.birthdate || profile.dob || profile.date_of_birth || extracted.birthdate || extracted.age);
  if (birthYear && year && Math.abs(year - birthYear) >= 2) return false;
  if (!birthYear || year) return true;
  const places = [
    ...(Array.isArray(extracted.addresses) ? extracted.addresses : []),
    ...(Array.isArray(extracted.places) ? extracted.places : []),
  ].map((place) => String(place || ''));
  if (!intakeState || places.length === 0) return false;
  const stateName = intakeState === 'TX' ? 'texas' : intakeState.toLowerCase();
  return places.some((place) => new RegExp(`\\b(${intakeState}|${stateName})\\b`, 'i').test(place));
};

export const collectReportFamilyRows = (report: IdentityPhotoReport): ReportFamilyRow[] => {
  const entity = asRecord(report.factDatabase?.entity);
  const subject = String(entity?.canonical_name || '');
  const birthYear = birthYearOf(entity?.birthdate);
  const addressBlob = (Array.isArray(entity?.addresses) ? entity.addresses : []).join(' ');
  const stateMatch = addressBlob.match(/\b(TX|Texas|Bexar)\b/i);
  const intakeState = stateMatch ? 'TX' : '';
  const candidates: Array<{ name: string; relation: string; strong: boolean }> = [];
  const add = (name: string, relation: string, strong: boolean) => {
    const cleaned = String(name || '').replace(/\s+/g, ' ').replace(/\s*\([^)]*\)\s*$/, '').trim();
    if (!looksLikePersonName(cleaned)) return;
    if (/see all|privacy|add friend|https?:/i.test(cleaned)) return;
    const explicit = explicitFamilyRelation(relation);
    candidates.push({
      name: cleaned,
      relation: explicit || (strong ? (String(relation || 'Relative').trim() || 'Relative') : 'Relative'),
      strong: strong || Boolean(explicit),
    });
  };
  for (const row of report.reportEvidence?.family || []) add(row.name, row.relation, true);
  for (const line of sectionBullets(report.content, 'Children')) {
    add(line.replace(/^child\s*:\s*/i, ''), 'Child', true);
  }
  for (const line of sectionBullets(report.content, 'Known relatives')) {
    const labeled = line.match(/^(spouse|parent|child|sibling|relative)\s*:\s*(.+)$/i);
    if (labeled) add(labeled[2], labeled[1], true);
    else add(line, 'Relative', true);
  }
  const signals = Array.isArray(report.factDatabase?.signals) ? report.factDatabase.signals : [];
  for (const signal of signals) {
    const kind = String(signal?.fact_type || '');
    const decision = String((signal as { decision?: string })?.decision || '').toLowerCase();
    if (decision === 'excluded' || decision === 'dismissed') continue;
    if (!/relative|family|spouse|parent|child|sibling|genealog/i.test(kind)) continue;
    const relation = /spouse/i.test(kind) ? 'Spouse'
      : /parent/i.test(kind) ? 'Parent'
        : /child/i.test(kind) ? 'Child'
          : /sibling/i.test(kind) ? 'Sibling'
            : 'Relative';
    add(personNameOf(signal?.value), relation, relation !== 'Relative');
  }
  const runs = [
    ...(Array.isArray(report.sourceChecksRuns) ? report.sourceChecksRuns : []),
    ...(Array.isArray(report.checkResults) ? report.checkResults : []),
  ];
  const buckets: Array<[string, string, boolean]> = [
    ['spouse_candidates', 'Spouse', true],
    ['children_candidates', 'Child', true],
    ['parent_candidates', 'Parent', true],
    ['sibling_candidates', 'Sibling', true],
    ['relative_candidates', 'Relative', false],
  ];
  for (const row of runs) {
    const record = asRecord(row);
    if (!record) continue;
    const live = asRecord(record.live_result) || record;
    const summary = asRecord(live.genealogy_summary);
    for (const [key, label, strong] of buckets) {
      const list = (summary && Array.isArray(summary[key]) ? summary[key] : live[key]) as unknown;
      if (!Array.isArray(list)) continue;
      for (const item of list) add(personNameOf(item), label, strong);
    }
    for (const profile of Array.isArray(live.structured_profiles) ? live.structured_profiles : []) {
      const card = asRecord(profile);
      if (!card || !profileAgreesWithSubject(card, birthYear, intakeState)) continue;
      const extracted = asRecord(card.extracted);
      const relatives = extracted && Array.isArray(extracted.relatives) ? extracted.relatives : [];
      const year = birthYearOf(card.birthdate || card.dob || extracted?.birthdate || extracted?.age);
      const yearMatched = Boolean(birthYear && year && Math.abs(year - birthYear) < 2);
      for (const relative of relatives) {
        const relativeRecord = asRecord(relative);
        const relation = String(relativeRecord?.relationship || relativeRecord?.relation || 'Relative');
        add(personNameOf(relative), relation, yearMatched || Boolean(explicitFamilyRelation(relation)));
      }
    }
    const scrape = asRecord(live.friends_scrape);
    const scrapedFriends = Array.isArray(scrape?.friend_names) ? scrape.friend_names : [];
    const scrapedCount = Number(scrape?.friends_count || scrape?.count || 0);
    const scrapedSampleIncomplete = scrapedCount >= 50 && scrapedFriends.length <= 3;
    if (!scrapedSampleIncomplete) {
      for (const friend of scrapedFriends) {
        const name = personNameOf(friend);
        if (sharesSurname(name, subject)) add(name, 'Relative', true);
      }
    }
    const corroboration = asRecord(scrape?.friend_corroboration);
    for (const match of Array.isArray(corroboration?.matches) ? corroboration.matches : []) {
      const matchRecord = asRecord(match);
      add(personNameOf(matchRecord?.relative || matchRecord?.friend), 'Relative', true);
    }
    const probes = Array.isArray(live.direct_profile_probes) ? live.direct_profile_probes : [];
    for (const probe of probes) {
      const profile = asRecord(asRecord(probe)?.facebook_profile);
      if (!profile) continue;
      const members = Array.isArray(profile.family_members) ? profile.family_members : [];
      for (const member of members) {
        const text = personNameOf(member);
        const relationMatch = text.match(/\(([^)]+)\)\s*$/);
        const name = relationMatch ? text.slice(0, relationMatch.index).trim() : text;
        const relation = relationMatch ? relationMatch[1] : 'Relative';
        add(name, relation, Boolean(explicitFamilyRelation(relation)));
      }
      const about = String(profile.about || profile.intro || profile.bio || '');
      const spouse = about.match(/\b(?:married to|wife|husband|spouse)\b\s*[:\-]?\s*([A-Z][a-z'-]+(?:\s+[A-Z][a-z'-]+){1,3})/i);
      if (spouse) add(spouse[1], 'Spouse', true);
      const child = about.match(/\b(?:son|daughter)\b\s*[:\-]?\s*([A-Z][a-z'-]+(?:\s+[A-Z][a-z'-]+){1,3})/i);
      if (child) add(child[1], 'Child', true);
      const listedFriends = Array.isArray(profile.friends) ? profile.friends : [];
      const friendsCount = Number(profile.friends_count || 0);
      if (!(friendsCount >= 50 && listedFriends.length <= 3)) {
        for (const friend of listedFriends) {
          const name = personNameOf(friend);
          if (sharesSurname(name, subject)) add(name, 'Relative', true);
        }
      }
    }
  }
  const rows: ReportFamilyRow[] = [];
  const seen = new Set<string>();
  const counts = new Map<string, number>();
  for (const candidate of candidates) {
    const key = candidate.name.toLowerCase();
    counts.set(key, (counts.get(key) || 0) + 1);
  }
  for (const candidate of candidates) {
    const key = candidate.name.toLowerCase();
    if (seen.has(key)) continue;
    if (!candidate.strong && (counts.get(key) || 0) < 2) continue;
    seen.add(key);
    const relation = explicitFamilyRelation(candidate.relation)
      || (candidate.strong ? candidate.relation : 'Relative');
    rows.push({ name: candidate.name, relation: relation || 'Relative' });
  }
  const friendNote = friendNoteFrom(report);
  if (friendNote && !rows.some((row) => row.relation === 'Friends')) {
    rows.push({ name: friendNote, relation: 'Friends' });
  }
  return rows;
};

export const buildReportEvidence = (
  report: IdentityPhotoReport,
): NonNullable<IdentityPhotoReport['reportEvidence']> => ({
  photoUrls: collectIdentityPhotosFromReport(report, { keepRawUrl: true }).map((photo) => photo.src).slice(0, 36),
  photoNotes: collectReportPhotoNotes(report).slice(0, 24),
  courts: collectReportCourtRows(report).slice(0, 40),
  contacts: collectReportContactRows(report).slice(0, 40),
  affiliations: collectReportAffiliationRows(report).slice(0, 40),
  family: collectReportFamilyRows(report).filter((row) => row.relation !== 'Friends').slice(0, 40),
  friendNote: friendNoteFrom(report),
});

export const reportContentKeepingBiography = (content: string, limit: number): string => {
  const text = String(content || '');
  const cap = Math.max(1000, limit);
  const match = text.match(/\n## Biography\b[\s\S]*$/i);
  if (!match || match.index == null) return text.slice(0, cap);
  const biography = match[0].trim();
  if (biography.length >= cap) return biography.slice(0, cap);
  const head = text.slice(0, match.index).trimEnd().slice(0, Math.max(0, cap - biography.length - 2));
  return `${head}\n\n${biography}`.trim();
};

export const photoAltLabel = (raw: string): string => {
  const blob = String(raw || '').trim().toLowerCase();
  if (!blob) return 'photo';
  if (/\b(headshot|portrait)\b/.test(blob)) return 'headshot';
  if (/\b(logo|icon|brand)\b/.test(blob)) return 'logo';
  if (/^(linkedin|facebook|instagram|twitter|x)$/.test(blob)) return 'photo';
  return blob.slice(0, 80) || 'photo';
};

export const linkifyEscapedHtml = (escaped: string): string => {
  const source = String(escaped || '');
  const withMarkdown = source.replace(
    /\[([^\]]+)\]\((https?:\/\/[^)\s]+)\)/g,
    (_all, text, url) => `<a href="${url}" target="_blank" rel="noopener noreferrer">${text}</a>`,
  );
  return withMarkdown.replace(
    /(?<!href="|src=")(https?:\/\/[^\s<&"]+)/g,
    (url) => `<a href="${url}" target="_blank" rel="noopener noreferrer">${url}</a>`,
  );
};

export const linkifyPlainTextToHtml = (text: string): string =>
  linkifyEscapedHtml(escapeHtml(text));

export const compactSocialHandle = (handle: string): string =>
  String(handle || '').toLowerCase().replace(/[^a-z0-9]/g, '');

export const isGenericNamePermutationHandle = (handle: string, names: string[] = []): boolean => {
  const raw = String(handle || '').trim().toLowerCase().replace(/\/+$/, '');
  const slug = compactSocialHandle(handle);
  if (!slug) return false;
  for (const name of names) {
    const tokens = String(name || '').toLowerCase().split(/[^a-z0-9]+/).filter((token) => token.length >= 2);
    if (tokens.length < 2) continue;
    const given = tokens[0];
    const last = tokens[tokens.length - 1];
    if (slug === `${given}${last}` || slug === `${last}${given}`) return true;
    if ([`${given}.${last}`, `${last}.${given}`, `${given}_${last}`, `${last}_${given}`].includes(raw)) {
      return true;
    }
  }
  return false;
};

export const facebookHandleFromText = (text: string): string => {
  const match = String(text || '').match(/facebook\.com\/([A-Za-z0-9._]+)/i);
  return match ? match[1].replace(/\.+$/, '').toLowerCase() : '';
};

export const collapseCompetingSocialCitations = (text: string, names: string[] = []): string => {
  const lines = String(text || '').split('\n');
  const facebookIndexes: number[] = [];
  const facebookHandles: string[] = [];
  lines.forEach((line, index) => {
    const handle = facebookHandleFromText(line);
    if (handle) {
      facebookIndexes.push(index);
      facebookHandles.push(handle);
    }
  });
  if (facebookIndexes.length < 2) return String(text || '');
  let winner = 0;
  facebookHandles.forEach((handle, idx) => {
    const current = facebookHandles[winner];
    const generic = isGenericNamePermutationHandle(handle, names) ? 1 : 0;
    const currentGeneric = isGenericNamePermutationHandle(current, names) ? 1 : 0;
    if (generic < currentGeneric) winner = idx;
    else if (generic === currentGeneric && compactSocialHandle(handle).length > compactSocialHandle(current).length) {
      winner = idx;
    }
  });
  const keepIndex = facebookIndexes[winner];
  return lines
    .filter((_line, index) => !facebookIndexes.includes(index) || index === keepIndex)
    .join('\n');
};

export const wrapOpenableImageHtml = (src: string, alt: string): string => {
  const safeSrc = escapeHtml(src);
  const safeAlt = escapeHtml(photoAltLabel(alt));
  return (
    `<a class="dashboard-bio-photo-link" href="${safeSrc}" target="_blank" rel="noopener noreferrer" download>`
    + `<img class="dashboard-bio-photo" alt="${safeAlt}" src="${safeSrc}" referrerpolicy="no-referrer" `
    + `onerror="var n=this.closest('li')||this.closest('a'); if(n) n.remove();" />`
    + `</a>`
  );
};

export const looksLikeBiographyChromeFragment = (text: string): boolean => {
  const raw = String(text || '').trim();
  if (!raw) return true;
  const blob = raw.toLowerCase();
  if (/https?:\/\//i.test(raw) && /(ancestry|familysearch|truepeoplesearch|spokeo|whitepages|beenverified)\.com/i.test(raw)) {
    return true;
  }
  if (/\bwas born\b/i.test(raw) && /https?:\/\//i.test(raw)) return true;
  if (/\badd friend\b/i.test(blob) || /\b\d+\s+friends\b/i.test(blob)) return true;
  if (/\b\d[\d,]*\s+profile views?\b/i.test(blob) || /\bdiscover who\b/i.test(blob)) return true;
  if (/\b[A-Z]{2,}[a-z]{1,8},\s*[A-Z]{2}\b/.test(raw)) return true;
  if (/\bsee all photos\b/i.test(blob) || /\bphotos?\s+see all\b/i.test(blob)) return true;
  if (/\b20\d{2}\s+photos?\b/i.test(blob) && /\b(privacy|see all)\b/i.test(blob)) return true;
  if (/\b\w+'s school\b/i.test(raw)) return true;
  if (/passion and drive|looking for those with passion/i.test(blob)) return true;
  if (/^[A-Z]{3,5},\s*[A-Z]{2}\.?$/.test(raw)) return true;
  if (
    /^\d{1,3}\s+[A-Z][a-z]+\s+[A-Z][a-z]+\b/.test(raw)
    && !/\b(st|street|ave|avenue|rd|road|blvd|dr|drive|ln|lane|ct|way|park|calle)\b/i.test(raw)
  ) {
    return true;
  }
  return false;
};

export const scrubResearchBiographyText = (text: string): string => {
  return String(text || '')
    .split('\n')
    .map((line) => {
      const trimmed = line.trim();
      if (!trimmed) return line;
      if (/^#{1,3}\s+/.test(trimmed) || /^[-*]\s+/.test(trimmed) || /^\d+\.\s+/.test(trimmed)) {
        return line;
      }
      const sentences = line.split(/(?<=\.)\s+/);
      const cleaned = sentences
        .map((sentence) => {
          let next = sentence.replace(/\bwas born\s+https?:\/\/\S+/gi, '').replace(/\s+/g, ' ').trim();
          if (!next) return '';
          next = next
            .split(/\s*;\s*/)
            .map((part) => part.trim().replace(/[.\u2026]{3,}$/g, '').trim())
            .filter((part) => part && !looksLikeBiographyChromeFragment(part))
            .join('; ');
          if (
            next
            && /^[\w .'-]+$/.test(next)
            && !/\b(residence|education|career|role|born|ofac|affiliation|relative)\b/i.test(next)
          ) {
            return '';
          }
          return next;
        })
        .filter(Boolean);
      return cleaned.join(' ');
    })
    .join('\n');
};

export type ResearchReportSection = { title: string; body: string };

const FOLLOW_UP_SECTION_RE = /follow-up research questions|preguntas de seguimiento/i;

export const isRichResearchReportSection = (title: string): boolean =>
  /^(biography|photos|biografía|fotos|follow-up research questions|preguntas de seguimiento)$/i.test(
    String(title || '').trim(),
  );

export const normalizeResearchReportSectionTitle = (title: string, language = 'en'): string => {
  const key = String(title || '').trim().toLowerCase();
  const enLabels: Record<string, string> = {
    'report overview': 'Report overview',
    summary: 'Executive Summary',
    'document parse receipt': 'Document Parse Receipt',
    'source check results': 'Source Check Results',
    findings: 'Findings',
    'analyst notes': 'Analyst Notes',
    'recommended next actions': 'Recommended Next Actions',
    'affinity relationship map': 'Affinity Relationship Map',
    biography: 'Biography',
    'follow-up research questions': 'Follow-up research questions',
    photos: 'Photos',
  };
  const esLabels: Record<string, string> = {
    'report overview': 'Resumen del reporte',
    summary: 'Resumen ejecutivo',
    'document parse receipt': 'Recibo de análisis documental',
    'source check results': 'Resultados de verificación de fuentes',
    findings: 'Hallazgos',
    'analyst notes': 'Notas del analista',
    'recommended next actions': 'Próximas acciones recomendadas',
    'affinity relationship map': 'Mapa de relaciones de afinidad',
    biography: 'Biografía',
    'follow-up research questions': 'Preguntas de seguimiento',
    photos: 'Fotos',
  };
  if (language === 'es') {
    return esLabels[key] || title;
  }
  return enLabels[key] || title;
};

export const splitResearchReportSections = (
  markdown: string,
  language = 'en',
): ResearchReportSection[] => {
  const rawContent = String(markdown || '').replace(/\r\n?/g, '\n');
  const headingRegex = /^(#{2,3})\s+(.+)$/;
  const sections: ResearchReportSection[] = [];
  let currentTitle = language === 'es' ? 'Resumen del reporte' : 'Report overview';
  let currentLines: string[] = [];
  const emptyFollowUp = language === 'es'
    ? 'No hay brechas biográficas abiertas en esta pasada.'
    : 'No open biography gaps in this pass.';

  const pushSection = () => {
    const body = currentLines.join('\n').trim();
    const filteredBody = body
      .split('\n')
      .filter((line) => {
        const rendered = String(line || '').trim();
        if (!rendered) {
          return false;
        }
        if (/^Report UUID:/i.test(rendered)) {
          return false;
        }
        return !/^[-*]\s*Fact:\s*[^:]+:\s*(?:N\/A|NA|Not\s+available|-)?\s*$/i.test(rendered);
      })
      .join('\n')
      .trim();
    if (!filteredBody) {
      if (FOLLOW_UP_SECTION_RE.test(currentTitle)) {
        sections.push({ title: currentTitle, body: emptyFollowUp });
      }
      return;
    }
    sections.push({ title: currentTitle, body: filteredBody });
  };

  for (const line of rawContent.split('\n')) {
    const headingMatch = line.match(headingRegex);
    if (headingMatch) {
      const level = headingMatch[1].length;
      const rawTitle = headingMatch[2].trim();
      const isFollowUp = FOLLOW_UP_SECTION_RE.test(rawTitle);
      if (level === 2 || isFollowUp) {
        pushSection();
        currentTitle = normalizeResearchReportSectionTitle(rawTitle, language);
        currentLines = [];
        continue;
      }
    }
    if (/^#\s+/.test(line)) {
      continue;
    }
    currentLines.push(line);
  }
  pushSection();

  if (sections.length === 0) {
    sections.push({
      title: language === 'es' ? 'Contenido del reporte' : 'Report content',
      body: rawContent,
    });
  }
  return sections;
};

export const filenameFromPhoto = (photo: ReportPhoto, index: number): string => {
  const base = photoAltLabel(photo.alt).replace(/[^a-z0-9]+/gi, '-').replace(/^-|-$/g, '') || 'photo';
  const extMatch = String(photo.src || '').match(/\.(jpe?g|png|gif|webp)(?:$|[?#])/i);
  const ext = extMatch ? extMatch[1].toLowerCase().replace('jpeg', 'jpg') : 'jpg';
  return `${base}-${index + 1}.${ext}`;
};

export async function downloadReportPhotos(
  photos: ReportPhoto[],
  fetchImpl: typeof fetch = fetch,
): Promise<number> {
  let saved = 0;
  for (let index = 0; index < photos.length; index += 1) {
    const photo = photos[index];
    try {
      const response = await fetchImpl(photo.src, { credentials: 'include' });
      if (!response.ok) continue;
      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = objectUrl;
      anchor.download = filenameFromPhoto(photo, index);
      anchor.rel = 'noopener';
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(objectUrl);
      saved += 1;
    } catch {
      // keep downloading the rest
    }
  }
  return saved;
}
